import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy, Linkedin, Loader2, Mail, RefreshCw, Search, SkipForward } from 'lucide-react';
import type { Contact, ContactStatus, SentChannel, Team, TeamMember } from '../../types';
import { CONTACT_STATUSES, LEAD_SEGMENTS } from '../../constants';
import { getTodayQueue, markLeadSent, markLeadStatus, skipQueueToday, ctaHintForSegment, attachLeadDraft, QUEUE_READY_STATUSES } from '../../services/outbound';
import { getTeamMembers } from '../../services/teams';
import LeadDrawer from '../Leads/LeadDrawer';

interface QueuePageProps {
  team: Team;
  userId: string;
  isAdmin?: boolean;
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

const QueuePage: React.FC<QueuePageProps> = ({ team, userId, isAdmin }) => {
  const [leads, setLeads] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [skippedToday, setSkippedToday] = useState(0);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [sentFor, setSentFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContactStatus | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [queue, teamMembers] = await Promise.all([
      getTodayQueue(team.id, { allTeams: isAdmin }),
      getTeamMembers(team.id),
    ]);
    setLeads(queue.leads);
    setTotal(queue.total);
    setSentCount(queue.sent);
    setSkippedToday(queue.skippedToday);
    setMembers(teamMembers);
    setLoading(false);
  }, [team.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const flashCopied = (key: string) => {
    setCopied(key);
    window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 1500);
  };

  const handleCopy = async (key: string, value: string) => {
    if (!value) return;
    const ok = await copyText(value);
    if (ok) flashCopied(key);
  };

  const handleSkip = async (id: string) => {
    setBusyId(id);
    await skipQueueToday(id);
    await load();
    setBusyId(null);
  };

  const handleApprove = async (id: string) => {
    setBusyId(id);
    await markLeadStatus(id, 'approved');
    await load();
    setBusyId(null);
  };

  const handleSent = async (id: string, channel: SentChannel) => {
    setBusyId(id);
    await markLeadSent(id, channel);
    setSentFor(null);
    await load();
    setBusyId(null);
  };

  const segmentLabel = (value: string | null) =>
    LEAD_SEGMENTS.find((s) => s.value === value)?.label || value || '—';

  const visibleLeads = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [c.full_name, c.first_name, c.last_name, c.company, c.email, c.title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, query, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const lead of leads) counts[lead.status] = (counts[lead.status] || 0) + 1;
    return counts;
  }, [leads]);

  return (
    <div className="animate-fadeIn max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Today’s Queue</h1>
          <p className="text-slate-400 text-sm mt-1">
            {total === 0
              ? 'No contacts imported yet. Add them on Leads or Import.'
              : `${leads.length} of ${total} still to work${sentCount ? ` · ${sentCount} already sent` : ''}${
                  skippedToday ? ` · ${skippedToday} skipped today` : ''
                }. Click a name to write the draft. LinkedIn never auto-sends.`}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative min-w-[220px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, company, email…"
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 text-sm rounded-lg ${
            !statusFilter ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
          }`}
        >
          All ready ({leads.length})
        </button>
        {QUEUE_READY_STATUSES.map((status) => {
          const count = statusCounts[status] || 0;
          const label = CONTACT_STATUSES.find((s) => s.value === status)?.label || status;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                statusFilter === status ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-orange-500" />
        </div>
      ) : visibleLeads.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center text-slate-400">
          {total === 0
            ? 'Import or add contacts on Leads — they all appear here until you mark them sent.'
            : leads.length === 0
              ? 'Everyone is either sent, skipped today, or not ready. Open Leads to see the full list.'
              : 'No one matches that search or filter.'}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleLeads.map((lead) => (
            <QueueCard
              key={lead.id}
              lead={lead}
              expanded={openId === lead.id}
              onToggle={() => setOpenId(openId === lead.id ? null : lead.id)}
              busy={busyId === lead.id}
              askingSent={sentFor === lead.id}
              copied={copied}
              onOpen={() => setEditing(lead)}
              onApprove={() => handleApprove(lead.id)}
              onAskSent={() => setSentFor(sentFor === lead.id ? null : lead.id)}
              onSent={(channel) => handleSent(lead.id, channel)}
              onSkip={() => handleSkip(lead.id)}
              onCopy={handleCopy}
              onSaved={async (updated) => {
                setLeads((current) => current.map((row) => (row.id === updated.id ? updated : row)));
              }}
              segmentLabel={segmentLabel}
            />
          ))}
        </div>
      )}

      {editing && (
        <LeadDrawer
          contact={editing}
          team={team}
          members={members}
          userId={userId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
};

interface QueueCardProps {
  lead: Contact;
  expanded: boolean;
  onToggle: () => void;
  busy: boolean;
  askingSent: boolean;
  copied: string | null;
  onOpen: () => void;
  onApprove: () => void;
  onAskSent: () => void;
  onSent: (channel: SentChannel) => void;
  onSkip: () => void;
  onCopy: (key: string, value: string) => void;
  onSaved: (updated: Contact) => Promise<void>;
  segmentLabel: (value: string | null) => string;
}

const QueueCard: React.FC<QueueCardProps> = ({
  lead,
  expanded,
  onToggle,
  busy,
  askingSent,
  copied,
  onOpen,
  onApprove,
  onAskSent,
  onSent,
  onSkip,
  onCopy,
  onSaved,
  segmentLabel,
}) => {
  const [subject, setSubject] = useState(lead.email_subject || '');
  const [emailBody, setEmailBody] = useState(lead.email_body || '');
  const [linkedinNote, setLinkedinNote] = useState(lead.linkedin_note || '');
  const [notes, setNotes] = useState(lead.research_notes || lead.notes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    setSubject(lead.email_subject || '');
    setEmailBody(lead.email_body || '');
    setLinkedinNote(lead.linkedin_note || '');
    setNotes(lead.research_notes || lead.notes || '');
  }, [lead.id, lead.email_subject, lead.email_body, lead.linkedin_note, lead.research_notes, lead.notes]);

  const name = lead.full_name || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unnamed';
  const copyPayload = [subject && `Subject: ${subject}`, emailBody].filter(Boolean).join('\n\n');

  const saveDraft = async () => {
    setSaving(true);
    const ok = await attachLeadDraft(
      lead.id,
      {
        email_subject: subject || null,
        email_body: emailBody || null,
        linkedin_note: linkedinNote || null,
        research_notes: notes || null,
      },
      lead.status
    );
    setSaving(false);
    if (ok) {
      setSavedAt('Saved');
      window.setTimeout(() => setSavedAt(null), 1500);
      await onSaved({
        ...lead,
        email_subject: subject || null,
        email_body: emailBody || null,
        linkedin_note: linkedinNote || null,
        research_notes: notes || null,
        status: lead.status === 'approved' ? lead.status : 'drafted',
      });
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className={`flex flex-wrap items-start justify-between gap-3 ${expanded ? 'mb-4' : ''}`}>
        <div className="flex items-start gap-2 min-w-0">
          <button onClick={onToggle} className="mt-1 text-slate-400 hover:text-white" aria-label={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <button onClick={onToggle} className="text-left min-w-0">
            <h2 className="text-lg font-semibold text-white">{name}</h2>
            <p className="text-sm text-slate-400">
              {lead.company || '—'} · {segmentLabel(lead.segment)} · {lead.country || lead.location || '—'} · {lead.status}
            </p>
            {lead.title && <p className="text-sm text-slate-500">{lead.title}</p>}
          </button>
          <button onClick={onOpen} className="text-xs text-orange-400 hover:text-orange-300 mt-1.5">
            Details
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {expanded && (
          <button
            disabled={busy || saving}
            onClick={saveDraft}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            {saving ? 'Saving…' : savedAt || 'Save draft'}
          </button>
          )}
          <button
            disabled={busy}
            onClick={onApprove}
            className="px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 text-white rounded-lg"
          >
            Approved
          </button>
          <button
            disabled={busy}
            onClick={onAskSent}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg"
          >
            Sent
          </button>
          <button
            disabled={busy}
            onClick={onSkip}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg"
          >
            <SkipForward size={14} />
            Skip today
          </button>
        </div>
      </div>

      {expanded && askingSent && (
        <div className="mb-4 p-3 bg-slate-800 rounded-lg flex flex-wrap gap-2">
          <span className="text-sm text-slate-400 mr-2">Marked sent on:</span>
          {(['email', 'linkedin', 'both'] as SentChannel[]).map((channel) => (
            <button
              key={channel}
              onClick={() => onSent(channel)}
              className="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg capitalize"
            >
              {channel}
            </button>
          ))}
        </div>
      )}

      {expanded && (
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-slate-500">Email draft</span>
            <div className="flex gap-2">
              {lead.email && (
                <a href={`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`} className="text-slate-400 hover:text-white">
                  <Mail size={14} />
                </a>
              )}
              <button onClick={() => onCopy(`${lead.id}-email`, lead.email || '')} className="text-slate-400 hover:text-white">
                {copied === `${lead.id}-email` ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-200 break-all">{lead.email || 'No email'}</p>
          <p className="text-xs text-amber-400">{ctaHintForSegment(lead.segment)}</p>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onBlur={saveDraft}
            placeholder="Email subject"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            onBlur={saveDraft}
            rows={6}
            placeholder="Write the email draft here…"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-y"
          />
          <button onClick={() => onCopy(`${lead.id}-email-body`, copyPayload)} className="text-xs text-orange-400 hover:text-orange-300">
            {copied === `${lead.id}-email-body` ? 'Copied email' : 'Copy email'}
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-slate-500">LinkedIn note</span>
            <div className="flex gap-2">
              {lead.linkedin_url && (
                <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white">
                  <Linkedin size={14} />
                </a>
              )}
              <button onClick={() => onCopy(`${lead.id}-li-url`, lead.linkedin_url || '')} className="text-slate-400 hover:text-white">
                {copied === `${lead.id}-li-url` ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-200 break-all">{lead.linkedin_url || 'No LinkedIn URL'}</p>
          <textarea
            value={linkedinNote}
            onChange={(e) => setLinkedinNote(e.target.value)}
            onBlur={saveDraft}
            rows={5}
            maxLength={300}
            placeholder="Write the connect note to paste into LinkedIn…"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-y"
          />
          <div className="flex items-center justify-between">
            <button onClick={() => onCopy(`${lead.id}-li-note`, linkedinNote)} className="text-xs text-orange-400 hover:text-orange-300">
              {copied === `${lead.id}-li-note` ? 'Copied note' : 'Copy connect note'}
            </button>
            <span className="text-xs text-slate-500">{linkedinNote.length}/250</span>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={saveDraft}
            rows={3}
            placeholder="Research notes / hook…"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-y"
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default QueuePage;
